import QRCode from 'qrcode'

/**
 * Generate QR code data URL for machine replenishment
 * @param {string} machineId - The machine identifier
 * @param {string} baseUrl - Base URL for the application (optional, defaults to current origin)
 * @returns {Promise<string>} QR code data URL
 */
export const generateReplenishQR = async (machineId, baseUrl) => {
  try {
    const url = baseUrl || window.location.origin
    const replenishUrl = `${url}/replenish/${machineId}`
    
    const qrCodeDataUrl = await QRCode.toDataURL(replenishUrl, {
      width: 300,
      margin: 2,
      color: {
        dark: '#000000',
        light: '#FFFFFF'
      },
      errorCorrectionLevel: 'M'
    })
    
    return qrCodeDataUrl
  } catch (error) {
    console.error('Error generating QR code:', error)
    throw new Error('Failed to generate QR code')
  }
}

/**
 * Generate printable QR code with machine information
 * @param {string} machineId - The machine identifier
 * @param {string} machineName - Human-readable machine name
 * @param {string} location - Machine location
 * @param {string} baseUrl - Base URL for the application
 * @returns {Promise<object>} QR code information object
 */
export const generatePrintableQR = async (machineId, machineName, location, baseUrl) => {
  try {
    const qrDataUrl = await generateReplenishQR(machineId, baseUrl)
    const replenishUrl = `${baseUrl || window.location.origin}/replenish/${machineId}`
    
    return {
      qrCodeDataUrl: qrDataUrl,
      machineId,
      machineName,
      location,
      replenishUrl,
      generatedAt: new Date().toISOString(),
      instructions: 'Scan this QR code with your mobile device to access the replenishment interface for this machine.'
    }
  } catch (error) {
    console.error('Error generating printable QR code:', error)
    throw new Error('Failed to generate printable QR code')
  }
}

/**
 * Generate QR code as SVG string
 * @param {string} machineId - The machine identifier
 * @param {string} baseUrl - Base URL for the application
 * @returns {Promise<string>} QR code SVG string
 */
export const generateReplenishQRSVG = async (machineId, baseUrl) => {
  try {
    const url = baseUrl || window.location.origin
    const replenishUrl = `${url}/replenish/${machineId}`
    
    const qrCodeSVG = await QRCode.toString(replenishUrl, {
      type: 'svg',
      width: 300,
      margin: 2,
      color: {
        dark: '#000000',
        light: '#FFFFFF'
      },
      errorCorrectionLevel: 'M'
    })
    
    return qrCodeSVG
  } catch (error) {
    console.error('Error generating QR code SVG:', error)
    throw new Error('Failed to generate QR code SVG')
  }
}

/**
 * Download QR code as image file
 * @param {string} machineId - The machine identifier
 * @param {string} machineName - Human-readable machine name
 * @param {string} baseUrl - Base URL for the application
 */
export const downloadQRCode = async (machineId, machineName, baseUrl) => {
  try {
    const qrDataUrl = await generateReplenishQR(machineId, baseUrl)
    
    // Create download link
    const link = document.createElement('a')
    link.href = qrDataUrl
    link.download = `${machineName || machineId}_replenish_qr.png`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  } catch (error) {
    console.error('Error downloading QR code:', error)
    throw new Error('Failed to download QR code')
  }
}

/**
 * Get replenishment URL for a machine
 * @param {string} machineId - The machine identifier
 * @param {string} baseUrl - Base URL for the application
 * @returns {string} Replenishment URL
 */
export const getReplenishUrl = (machineId, baseUrl) => {
  const url = baseUrl || window.location.origin
  return `${url}/replenish/${machineId}`
}

/**
 * Validate machine ID format
 * @param {string} machineId - The machine identifier to validate
 * @returns {boolean} True if valid machine ID format
 */
export const isValidMachineId = (machineId) => {
  if (!machineId || typeof machineId !== 'string') {
    return false
  }
  
  // Machine ID should be alphanumeric and may contain hyphens or underscores
  const machineIdRegex = /^[a-zA-Z0-9_-]+$/
  return machineIdRegex.test(machineId) && machineId.length >= 3 && machineId.length <= 20
}